import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Music, Heart, Users, Sparkles, ArrowRight, Play } from "lucide-react";
import { Link } from "react-router-dom";

const kpopGroups = [
  {
    name: "Stray Kids",
    shortName: "SKZ",
    fandom: "STAY",
    link: "/study-with-skz/",
    gradient: "from-red-500/20 to-orange-500/20",
    borderColor: "border-red-500/30 hover:border-red-500/60",
  },
  {
    name: "ITZY",
    shortName: "ITZY",
    fandom: "MIDZY",
    link: "/study-with-itzy/",
    gradient: "from-purple-500/20 to-pink-500/20",
    borderColor: "border-purple-500/30 hover:border-purple-500/60",
  },
  {
    name: "BLACKPINK",
    shortName: "BP",
    fandom: "BLINK",
    link: "/study-with-blackpink/",
    gradient: "from-pink-500/20 to-rose-500/20",
    borderColor: "border-pink-500/30 hover:border-pink-500/60",
  },
  {
    name: "aespa",
    shortName: "æspa",
    fandom: "MY",
    link: "/study-with-aespa/",
    gradient: "from-violet-500/20 to-indigo-500/20",
    borderColor: "border-violet-500/30 hover:border-violet-500/60",
  },
  {
    name: "TWICE",
    shortName: "TWICE",
    fandom: "ONCE",
    link: "/study-with-twice/",
    gradient: "from-amber-500/20 to-yellow-500/20",
    borderColor: "border-amber-500/30 hover:border-amber-500/60",
  },
  {
    name: "ATEEZ",
    shortName: "ATZ",
    fandom: "ATINY",
    link: "/study-with-ateez/",
    gradient: "from-blue-500/20 to-cyan-500/20",
    borderColor: "border-blue-500/30 hover:border-blue-500/60",
  },
];

const StudyWithKpop = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study With Kpop - K-Pop Themed Study Rooms"
        description="Study with your favorite K-Pop artists. Join virtual study rooms for Stray Kids, ITZY, BLACKPINK, aespa, TWICE, and ATEEZ fans worldwide."
        keywords="study with kpop, kpop study room, study with stray kids, study with itzy, study with blackpink, kpop playlist study"
        canonicalUrl="/study-with-kpop/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">STUDY WITH YOUR FAVORITE ARTISTS</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With <span className="text-gradient">K-Pop</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join virtual study rooms inspired by your favorite K-Pop artists. Stay motivated, focused, and productive while surrounded by your fandom community.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Studying <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium">Free Forever</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="font-medium">Global Fandoms</span>
                </div>
              </div>
            </div>

            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* K-Pop Groups Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Choose Your <span className="text-gradient">Artist</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Study alongside fellow fans in themed virtual rooms. Pick your favorite group and join the community.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {kpopGroups.map((group, index) => (
                <Link 
                  key={group.name} 
                  to={group.link}
                  className="block"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  <Card 
                    className={`p-8 bg-gradient-to-br ${group.gradient} backdrop-blur-sm ${group.borderColor} transition-all hover:scale-105 animate-fade-in cursor-pointer group`}
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Music className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-foreground mb-2">
                      Study with {group.name}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Join fellow {group.fandom}s in a focused study environment inspired by {group.shortName}.
                    </p>
                    <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                      <span>Enter Room</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Study with K-Pop */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Why Study With <span className="text-gradient">K-Pop?</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                {
                  icon: <Heart className="w-6 h-6 text-primary" />,
                  title: "Fandom Motivation",
                  desc: "Channel your love for your favorite artists into productive energy. Study hard like your idols work hard."
                },
                {
                  icon: <Users className="w-6 h-6 text-primary" />,
                  title: "Community Support",
                  desc: "Connect with fellow fans who share your passion. Motivate each other and build study friendships."
                },
                {
                  icon: <Music className="w-6 h-6 text-primary" />,
                  title: "Themed Ambiance",
                  desc: "Immerse yourself in an atmosphere inspired by your favorite group while staying focused on your goals."
                },
                {
                  icon: <Sparkles className="w-6 h-6 text-primary" />,
                  title: "Achieve Your Dreams",
                  desc: "Just like your idols pursue their dreams, use CSW to pursue yours with dedication and focus."
                },
              ].map((item, index) => (
                <Card 
                  key={index} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all animate-fade-in"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">Your Fandom?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of K-Pop fans who study together on CSW. Free forever, no limits, just pure productivity with your favorite artists as inspiration.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Start Studying Free <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyWithKpop;
